// src/components/goal-settings/KpaList.jsx
import React, { useEffect, useMemo, useState } from 'react';

import {
    listKpas,
    createKpa,
    updateKpa,
    deleteKpa,
} from '../../api/kpas';

import { fetchManagerKpas, cascadeManagerKpas } from '../../api/kpas';

import KpaCreateModal from './KpaCreateModal';
import KpaModal from './KpaModal';
import KpaCascadeModal from './KpaCascadeModal';
import KPIList from "./KpiList";

function sumWeights(rows) {
    return (rows || []).reduce((acc, r) => acc + Number(r?.weight ?? 0), 0);
}

export default function KpaList({
                                    contractId,
                                    kpaWeighting = 90,
                                    onTotalsChange,
                                }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const [openIds, setOpenIds] = useState(new Set());

    // Modals
    const [showCreate, setShowCreate] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [showCascade, setShowCascade] = useState(false);

    async function refresh() {
        if (!contractId) return;
        setLoading(true); setErr('');
        try {
            const data = await listKpas(contractId);
            setRows(data || []);
        } catch (e) {
            setErr(e?.response?.data?.message || 'Failed to load KPAs.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refresh(); }, [contractId]);

    const totalWeight = useMemo(() => sumWeights(rows), [rows]);

    useEffect(() => {
        onTotalsChange?.({ kpaTotal: totalWeight });
    }, [totalWeight, onTotalsChange]);

    const toggle = (id) => {
        setOpenIds(prev => {
            const s = new Set(prev);
            const k = String(id);
            s.has(k) ? s.delete(k) : s.add(k);
            return s;
        });
    };
    const expandAll = () => setOpenIds(new Set(rows.map(r => String(r.id))));
    const collapseAll = () => setOpenIds(new Set());

    // CRUD
    const onCreate = async (payload) => {
        await createKpa({ ...payload, contract_id: contractId });
        setShowCreate(false);
        await refresh();
    };
    const onUpdate = async (id, payload) => {
        await updateKpa(id, payload);
        setEditRow(null);
        await refresh();
    };
    const onDelete = async (id) => {
        if (!window.confirm('Delete this KPA?')) return;
        await deleteKpa(id);
        await refresh();
    };

    // Validate (local; approval enforces 100 later)
    const onValidate = () => {
        const ok = totalWeight === Number(kpaWeighting);
        const msg = ok
            ? `✅ KPA weights are valid: ${totalWeight} == ${kpaWeighting}`
            : `⚠️ KPA weights mismatch: total ${totalWeight} != target ${kpaWeighting}`;
        alert(msg);
    };

    // Cascade submit
    const handleCascadeSubmit = async ({ managerKpaIds }) => {
        await cascadeManagerKpas(contractId, {
            mode: 'selected',
            kpa_ids: managerKpaIds.map(Number),
        });
        setShowCascade(false);
        await refresh();
    };

    return (
        <div className="card">
            <div className="card-header">
                <h5 className="mb-0">Key Performance Areas</h5>
            </div>
            <div className="card-body">
                <div className="reviews-table">
                    {/* Toolbar */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <div className="ms-auto d-flex gap-2">
                            <button className="btn btn-sm btn-outline-secondary" onClick={expandAll}>Expand all</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>Collapse all</button>

                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowCreate(true)}
                            >
                                Add KPA
                            </button>

                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowCascade(true)}
                            >
                                Cascade KPAs
                            </button>

                            <button className="btn btn-sm btn-outline-success" onClick={onValidate}>
                                Validate Weights
                            </button>
                        </div>
                    </div>

                    {err && <div className="alert alert-danger py-1 mb-2">{err}</div>}

                    <table>
                        <thead>
                        <tr className="rt-head-top">
                            <th className="rt-counter">#</th>
                            <th className="rt-name">Key Performance Area</th>
                            <th>Timeframe</th>
                            <th>Alignment</th>
                            <th>Weight</th>
                            <th className="rt-actions">Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="text-center text-muted py-3">Loading…</td></tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted py-3">No KPAs yet.</td></tr>
                        )}

                        {!loading && rows.map((r, idx) => {
                            const open = openIds.has(String(r.id));
                            const counter = `KPA ${String(idx + 1).padStart(2, '0')}`;
                            const cascaded = Boolean(r.parent_id);

                            return (
                                <React.Fragment key={r.id}>
                                    <tr className="rt-kpa-row">
                                        <td className="rt-counter">
                                            <button className="rt-kpa-toggle" onClick={() => toggle(r.id)}>
                                                <span className="me-2">{open ? '▾' : '▸'}</span>{counter}
                                            </button>
                                        </td>

                                        <td className="rt-name">
                                            {r.name}
                                            {cascaded && <span className="ms-2 badge bg-secondary">Cascaded</span>}
                                        </td>

                                        <td className="rt-num">{r.timeframe?.name ?? '—'}</td>
                                        <td className="rt-num">{r.alignment ?? '—'}</td>
                                        <td className="rt-num">{r.weight}</td>

                                        <td className="rt-actions">
                                            <div className="d-flex gap-1 justify-content-end">
                                                <button className="rt-btn rt-btn-primary" onClick={() => setEditRow(r)}>Edit</button>
                                                <button className="rt-btn btn-danger" onClick={() => onDelete(r.id)}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>

                                    {open && (
                                        <tr>
                                            <td colSpan={6} style={{ background: '#fafafa', padding: '8px 12px' }}>
                                                <KPIList
                                                    kpa={r}
                                                    canEdit={true /* set false when a manager is viewing someone else's contract */}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>

                        <tfoot>
                        <tr>
                            <td colSpan={4} className="text-end fw-semibold">KPA Total</td>
                            <td className="rt-num fw-semibold">{totalWeight}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td colSpan={4} className="text-end text-muted">Target (contract KPA weighting)</td>
                            <td className="rt-num text-muted">{kpaWeighting}</td>
                            <td></td>
                        </tr>
                        </tfoot>
                    </table>

                    {/* CREATE */}
                    <KpaCreateModal
                        show={showCreate}
                        open={showCreate}
                        visible={showCreate}
                        contractId={contractId}
                        onClose={() => setShowCreate(false)}
                        onCreated={onCreate}
                    />

                    {/* EDIT */}
                    <KpaModal
                        show={!!editRow}
                        open={!!editRow}
                        visible={!!editRow}
                        kpa={editRow}
                        onClose={() => setEditRow(null)}
                        onSaved={(payload) => {
                            if (editRow?.id) onUpdate(editRow.id, payload);
                        }}
                    />

                    {/* CASCADE (contract-scoped, not row-scoped) */}
                    <KpaCascadeModal
                        show={showCascade}
                        open={showCascade}
                        visible={showCascade}
                        contractId={contractId}
                        // numeric guards for math inside the modal
                        currentTotal={Number.isFinite(totalWeight) ? Number(totalWeight) : 0}
                        kpaWeighting={Number.isFinite(Number(kpaWeighting)) ? Number(kpaWeighting) : 90}
                        onClose={() => setShowCascade(false)}
                        onSubmit={({ managerKpaIds }) => handleCascadeSubmit({ managerKpaIds })}
                    />
                </div>
            </div>
        </div>

    );
}